// src/components/Hero.jsx
import React from 'react';
import { motion } from 'framer-motion';
import { HiArrowRight, HiChip, HiCode } from 'react-icons/hi';
// Note: hero_tech_visual_1767809842472.png is in the .gemini directory, 
// but since I'm implementing it for the user, I'll refer to it as an asset.
// For now, I'll use a placeholder or describe how to use the generated image.
// Actually, I should probably use a relative path if it's moved, 
// but since I can't move it myself easily to the project's assets without knowing the structure,
// I'll assume it will be placed in src/assets/hero-visual.png
import heroVisual from '../assets/hero_visual.png'; // User will need to ensure this exists or I'll use a placeholder URL if I can't move it.

const Hero = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] },
    },
  };

  const imageVariants = {
    hidden: { scale: 0.8, opacity: 0, rotate: -5 },
    visible: {
      scale: 1,
      opacity: 1,
      rotate: 0,
      transition: { duration: 1.2, ease: [0.22, 1, 0.36, 1], delay: 0.4 },
    },
  };

  const floatingVariants = {
    animate: {
      y: [0, -15, 0],
      transition: {
        duration: 4,
        repeat: Infinity,
        ease: "easeInOut"
      }
    }
  };

  return (
    <section id="home" className="relative h-screen flex items-center bg-slate-50 overflow-hidden">
      {/* --- Background Elements --- */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Soft Gradient Background */}
        <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-b from-slate-50 via-blue-50/30 to-slate-50" />

        {/* Animated Glows - Light Theme */}
        <motion.div
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.3, 0.5, 0.3],
            x: [0, 50, 0],
            y: [0, 30, 0],
          }}
          transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut' }}
          className="absolute -top-1/4 -left-1/4 w-[600px] h-[600px] bg-primary-200/40 rounded-full blur-[100px]"
        />
        <motion.div
          animate={{
            scale: [1.2, 1, 1.2],
            opacity: [0.2, 0.4, 0.2],
            x: [0, -50, 0],
            y: [0, -30, 0],
          }}
          transition={{ duration: 12, repeat: Infinity, ease: 'easeInOut', delay: 2 }}
          className="absolute -bottom-1/4 -right-1/4 w-[700px] h-[700px] bg-blue-200/30 rounded-full blur-[120px]"
        />

        {/* Grid Pattern - Subtler for Light Theme */}
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#e2e8f0_1px,transparent_1px),linear-gradient(to_bottom,#e2e8f0_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_100%)] opacity-20" />
      </div>

      <div className="container-custom h-full relative z-10 px-4 sm:px-4 lg:px-8 flex items-center">
        <div className="w-full flex flex-col lg:flex-row items-center gap-12 lg:gap-16 pt-16">

          {/* --- Left Content: Text & CTA --- */}
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="flex-1 text-left"
          >


            {/* Main Heading */}
            <motion.h1 variants={itemVariants} className="text-4xl md:text-6xl lg:text-7xl font-bold text-slate-900 mb-6 leading-[1.1]">
              Elevating Growth with <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary-600 via-blue-600 to-cyan-500">
                EFA Tech Solutions
              </span>
            </motion.h1>

            {/* Subtext */}
            <motion.p variants={itemVariants} className="text-lg md:text-xl text-slate-600 mb-8 max-w-2xl leading-relaxed">
              We help visionary businesses transform ideas into powerful digital experiences with cutting-edge technology and human-centric design.            </motion.p>

            {/* Buttons */}
            <motion.div variants={itemVariants} className="flex flex-col sm:flex-row items-stretch sm:items-center gap-1 mb-8 lg:mb-6">
              <motion.button
                whileHover={{ scale: 1.05, boxShadow: "0 10px 20px -10px rgba(59, 130, 246, 0.5)" }}
                whileTap={{ scale: 0.95 }}
                className="bg-primary-600 hover:bg-primary-700 text-white px-8 py-4 rounded-xl font-bold flex items-center justify-center transition-all group shadow-lg shadow-primary-500/20"
                onClick={() => document.getElementById('services').scrollIntoView({ behavior: 'smooth' })}
              >
                Our Services
                <HiArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" />
              </motion.button>

            </motion.div>
          </motion.div>

          {/* --- Right Content: Visual & Interactive Cards --- */}
          <motion.div
            variants={imageVariants}
            initial="hidden"
            animate="visible"
            className="flex-1 relative w-full lg:max-w-none flex justify-center lg:justify-end"
          >
            {/* Main Visual Container */}
            <motion.div
              whileHover={{ rotateY: 5, rotateX: -3, scale: 1.02 }}
              style={{ perspective: 1000 }}
              className="relative w-full h-[40vh] md:h-[60vh] lg:h-[75vh] max-h-[650px] rounded-3xl overflow-hidden shadow-2xl shadow-slate-200 border border-white bg-white/50 backdrop-blur-3xl group"
            >
              <img
                src={heroVisual}
                alt="Tech Innovation Visual"
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-[2s] ease-out"
                onError={(e) => {
                  e.target.src = "https://images.unsplash.com/photo-1635332398717-93521cc4e9db?q=80&w=2070&auto=format&fit=crop";
                }}
              />
              <div className="absolute inset-0 bg-gradient-to-tr from-white/20 via-transparent to-transparent opacity-40" />
            </motion.div>

            {/* Floating Info Cards */}
            <motion.div
              variants={floatingVariants}
              animate="animate"
              className="absolute -top-6 -left-6 md:-left-12 bg-white/80 backdrop-blur-xl border border-slate-200/50 p-4 rounded-2xl shadow-xl flex items-center gap-4 max-w-[200px]"
            >
              <div className="w-10 h-10 rounded-xl bg-primary-100 flex items-center justify-center text-primary-600">
                <HiCode className="w-6 h-6" />
              </div>
              <div>
                <p className="text-slate-900 text-sm font-bold leading-tight">Agile <br />Dev</p>
              </div>
            </motion.div>

            <motion.div
              animate={{
                y: [0, 20, 0],
                transition: { duration: 5, repeat: Infinity, ease: "easeInOut", delay: 1 }
              }}
              className="absolute top-1/2 -right-6 md:-right-10 bg-white/80 backdrop-blur-xl border border-slate-200/50 p-4 rounded-2xl shadow-xl flex items-center gap-4 max-w-[220px]"
            >
            </motion.div>

            <motion.div
              animate={{
                y: [0, -10, 0],
                transition: { duration: 4.5, repeat: Infinity, ease: "easeInOut", delay: 0.5 }
              }}
              className="absolute -bottom-6 left-1/4 bg-white/80 backdrop-blur-xl border border-slate-200/50 p-4 rounded-2xl shadow-xl flex items-center gap-4"
            >
              <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center text-purple-600">
                <HiChip className="w-6 h-6" />
              </div>
              <div>
                <p className="text-slate-900 text-sm font-bold">AI Analytics Integrated</p>
              </div>
            </motion.div>

            {/* Background Decorative Circles */}
            <div className="absolute -z-10 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[120%] h-[120%] border border-slate-200 rounded-full" />
            <div className="absolute -z-10 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[140%] h-[140%] border border-slate-100 rounded-full opacity-50" />
          </motion.div>
        </div>
      </div>

      {/* --- Scroll Indicator --- */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0 }}
        className="absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 cursor-pointer group/scroll"
        onClick={() => document.getElementById('services')?.scrollIntoView({ behavior: 'smooth' })}
      >

      </motion.div>
    </section>
  );
};

export default Hero;
